import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, Renderer2, ViewChild, forwardRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { DatePipe, IntlService } from '@progress/kendo-angular-intl';
import { CLIENTS_ROUTES } from '@saliente/clients';
import { ReleaseNotesDialogService } from '@saliente/common-release-notes';
import { EXPERT_ROUTES } from '@saliente/expert';
import { $, MessageBoxService, UID, getFileIcon, markdown } from '@saliente/library';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, forkJoin, fromEvent, of } from 'rxjs';
import { catchError, debounceTime, first, map, take } from 'rxjs/operators';
import { CLIENT_FORMS_ROUTES } from 'src/app/client-forms';
import { ESHOP_ROUTES } from 'src/app/eshop/eshop.routes.constants';
import { FormComponentModel } from 'src/app/client-forms/client-forms.models';
import { ClientFormsService } from 'src/app/client-forms/client-forms.service';
import { PredefinedUserZone } from '../auth/auth.models';
import { AuthService } from '../auth/auth.service';
import { COMMON_ROUTES } from '../common.routes.constants';
import { AppConstants } from '../constants/app.constants';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { DocumentIndentificationModel } from '../document-display/document-display.models';
import { ConfigService } from '../general/config.service';
import { DownloadService } from '../general/download.service';
import { PageContentComponent } from '../general/pagecontent.component';
import { RestService } from '../general/rest.service';
import { ApplicationLinkService } from '../services/application-link.service';
import { ApplicationLinkCode } from '../services/application-link-codes';
import { ChatStatus } from './chat.enums';
import { ChatAiFeedbackModel, ChatEditCommentModel, ChatMessageAttachedDocumentModel, ChatMessageModel, ChatModel, ChatTypeModel, ClientChangeRequestProcessStatusModel, ClientChatCommentInputModel, ClientChatInputModel, UploadFileModel } from './chat.models';
import { ChatService } from './chat.service';
import * as Meetings from './meetings/adapters/meetings-api.generated';
import { ExpertInfoPersonModel, MeetingRequestDataHistoryModel, MeetingRequestDataModel, MeetingRequestModel } from './meetings/meetings.model';
import { showAdminConnectClientMessage, showPayrollConnectClientMessage } from 'src/app/common/constants';

const nativeElement = (component: any): any => {
	if (component) {
		return component.nativeElement;
	}
	return null;
};

const clientDocumentRouteUrl = `${CLIENTS_ROUTES.ROOT}/${CLIENTS_ROUTES.ACC_DOCUMENTS}/`;
const expertDocumentRouteUrl = `${EXPERT_ROUTES.ROOT}/${EXPERT_ROUTES.DOCUMENTS}/`;
const payrollDocumentRouteUrl = `${CLIENTS_ROUTES.ROOT}/${CLIENTS_ROUTES.PAYROLL_DOCUMENTS}/`;
const microProfitFormRouteUrl = { 'form': `${CLIENT_FORMS_ROUTES.ROOT}/`, 'microProfit': `/${CLIENT_FORMS_ROUTES.MICRO_PROFIT_FORM}/`};
const duPFAFormRouteUrl = { 'form': `${CLIENT_FORMS_ROUTES.ROOT}/`, 'duPFA': `/${CLIENT_FORMS_ROUTES.DU_PFA_FORM}/`};
const mp2024FormRouteUrl = { 'form': `${CLIENT_FORMS_ROUTES.ROOT}/`, 'mp2024': `/${CLIENT_FORMS_ROUTES.MP2024_FORM}/`};
const eshopOrderRouteUrl = `${ESHOP_ROUTES.ROOT}/${ESHOP_ROUTES.ORDERS}/`;
const meetingRouteUrl = `${COMMON_ROUTES.MEETING_1}/`;
const meetingRequestRouteUrl = { 'base': `${COMMON_ROUTES.MEETING}/${CLIENTS_ROUTES.ROOT_1}/`, 'meetings': '/meetings/'};

const DEFAULT_PDF_ZOOM = 0.98;

@Component({
    selector: 'chat',
    templateUrl: 'chat.component.html',
    styleUrls: ['./chat.component.scss'],
    providers: [{ provide: PageContentComponent, useExisting: forwardRef(() => ChatComponent) }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChatComponent extends PageContentComponent implements OnInit, OnDestroy {
	private _messagesContainerEl: any;
	@ViewChild('messagesContainer', { static: false }) set messagesContainer(value: any) {
		const valueEl = nativeElement(value);
		if (this._messagesContainerEl !== valueEl) {
			this.unhookMessagesContainer();
			this._messagesContainerEl = valueEl;
			this.hookMessagesContainer();
		}
		this.windowLayoutProps();
		this.resizeTextInput();
	}

	private _unreadMessagesEl: any;
	@ViewChild('unreadMessages', { static: false }) set unreadMessages(value: any) {
		const valueEl = nativeElement(value);
		if (this._unreadMessagesEl !== valueEl) {
			this._unreadMessagesEl = valueEl;
		}
	}

	private _prevMessagesLoaderEl: any;
	@ViewChild('prevMessagesLoader', { static: false }) set prevMessagesLoader(value: any) {
		const valueEl = nativeElement(value);
		if (this._prevMessagesLoaderEl !== valueEl) {
			this._prevMessagesLoaderEl = valueEl;
		}
	}

	private _nextMessagesLoaderEl: any;
	@ViewChild('nextMessagesLoader', { static: false }) set nextMessagesLoader(value: any) {
		const valueEl = nativeElement(value);
		if (this._nextMessagesLoaderEl !== valueEl) {
			this._nextMessagesLoaderEl = valueEl;
		}
	}

	@ViewChild('filesAttachment') filesAttachment: ElementRef;

	public isClientAdmin: boolean = true;
	public isExpert: boolean = false;
	public isHrExpert: boolean = false;
	// public isSpecialCareOps: boolean = false;

	private _chatRoomSearch: string;
	public get chatRoomSearch(): string {
		return this._chatRoomSearch;
	}
	public set chatRoomSearch(value: string) {
		if (value !== this._chatRoomSearch) {
			this._chatRoomSearch = value;
			this.delayedFilterRooms();
		}
	}

	public chats: ChatModel[] = [];
	public filteredChats: ChatModel[] = [];
	public filteredChatsTemp: ChatModel[] = [];
	public chatsSubscription: Subscription;
	public chatTypes: ChatTypeModel[] = [];
	public filteredChatTypes: ChatTypeModel[] = [];
	public chatTypesSubscription: Subscription;
	public messages: ChatMessageModel[] = [];
	public messagesSubscription: Subscription;
	public zoneSubscription: Subscription;
	private resizeSubscription: Subscription;
	private scrollSubscription: Subscription;
	private dataChangedSubscription: Subscription;

	public loadingInitialChunk = false;
	public userEid: string;
	private loadingPrevChunk = false;
	public prevChunkEid: string;
	private loadingNextChunk = false;
	public nextChunkEid: string;
	public displayMessageId: number;

	public ChatStatus = ChatStatus;
	public filterStatus: string;
	public now: number;
	public isFromRefresh: boolean = false;
	public showMarkChatAnsweredDialog: boolean;
	public chatOpInput: ChatEditCommentModel;

	public authorIsKeez: boolean;
	public authorIsExpert: boolean;
	public isZeus: boolean;

	public replyToMessage: ChatMessageModel = null;

	// Connect assistant (#12438). The assistant answers server-side, so the component only asks for an
	// answer and collects verdicts on the ones already posted. Verdicts are keyed by messageId.
	public aiFeedbackByMessageId: { [messageId: number]: ChatAiFeedbackModel[] } = {};
	// An expert's or Keez user's verdict goes through a dialog, because it carries a comment - optional
	// on a like, required on a dislike (#12691). A client's verdict is a single click.
	public aiFeedbackDialog: { message: ChatMessageModel, vote: string, comment: string } = null;
	// One verdict at a time: the icons and the dialog's Save stay inert from the moment a verdict is
	// sent until the server answers. Without it a second click recorded a second feedback row for the
	// same answer, with nothing on screen saying the first was still in flight.
	public aiFeedbackSaving: boolean = false;
	// Automatic answers are a per-client feature (#12690), so the on-demand button follows the flag of
	// the room's client rather than the selected company.
	public aiAnswersEnabled: boolean = false;

	private _message = '';
	public get message(): string {
		return this._message;
	}
	public set message(value: string) {
		if (this._message !== value) {
			this._message = value;
			this.resizeTextInput();
		}
	}

	public showDocumentDialog: boolean = false;
	public documentIndentification: DocumentIndentificationModel;

	public showMicroProfitForm: boolean = false;
	public clientEid: string;
	public instance: string;
	public is202503Form: boolean; 
	public showDuPFAForm: boolean = false;
	public showDuPFA2024Form: boolean = false;
	public showDuPFA2025Form: boolean = false;
	public showMp2024Form: boolean = false;
	public showMp2025P2MForm: boolean = false;
	public showMp20242025M2PForm: boolean = false;

	public startMeetingDisabled: boolean = false;
	public showExpertStartMeeting: boolean = false;
	public showClientStartMeeting: boolean = false;
	
	// meeting calendar
	public showMeetingCalendar: boolean = false;
	public fromProposal: boolean;
	public meetingRequest: MeetingRequestModel ; //calendar time slots

	public showMeetingRequest: boolean = false;
	public meetingRequestData: MeetingRequestDataModel; //info
	public expertInfoPersonModel: ExpertInfoPersonModel;
	public meetingInfoLastHistory: MeetingRequestDataHistoryModel[] = [];
	public showMeetingInfo: boolean = false;

	public showScrollToBottom: boolean = false;

	public clientChangeRequestProcess: ClientChangeRequestProcessStatusModel;
	public clientChangeRequestProcessString: string;

	public routes: FormComponentModel[];
	public filesForUpload: UploadFileModel[] = [];
	public allowedFileTypes = new Map<string, string>([
		['application/pdf', 'pdf'],
		['application/msword', 'doc'],
		['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx'],
		['application/vnd.ms-excel', 'xls'],
		['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx'],
		['application/vnd.ms-powerpoint', 'ppt'],
		['application/vnd.openxmlformats-officedocument.presentationml.presentation', 'pptx'],
		/*['text/plain', 'txt'],
		['text/csv', 'csv']*/
	  ]);

	public imageFileType = ['png', 'jpg', 'jpeg'];

	public showAttachmentPreview: boolean = false;
	public selectedAttachment: ChatMessageAttachedDocumentModel;
	public selectedMessage: ChatMessageModel;
	public attachmentPreviewWidth: number;
	public attachmentPreviewHeight: number;

	/* Pdf Viewer */
	public pdfSrc: string;
    public zoom: number = DEFAULT_PDF_ZOOM;
    public page: number = 1;
	public isRenderText: boolean = true;
	public fullScreen: boolean =  false;
	public isDragging: boolean = false;

	constructor(
		private authService: AuthService,
		public chatService: ChatService,
		private zone: NgZone,
		private cdr: ChangeDetectorRef,
		private renderer: Renderer2,
		private router: Router,
		private toastr: ToastrService,
		public intlService: IntlService,
		private restService: RestService,
		public messageBoxService: MessageBoxService,
		public clientFormsService: ClientFormsService,
		private configService: ConfigService,
		private elementRef: ElementRef,
		private downloadService: DownloadService,
		private releaseNotesDialogService: ReleaseNotesDialogService,
		private featureFlagsService: FeatureFlagsService,
		private applicationLinkService: ApplicationLinkService
	) {
		super(authService);
		this.rootCssClass = 'chat-page';
		this.isClientAdmin = this.authService.selectedZoneCode == PredefinedUserZone.Administrator;
		this.isExpert = this.authService.selectedZoneCode == PredefinedUserZone.Expert;
		this.isHrExpert = this.authService.selectedZoneCode == PredefinedUserZone.HrExpert;
		// CM 20250424 SPECIAL_CARE_OPS should no longer be used
		// this.isSpecialCareOps = this.authService.isInRole('SPECIAL_CARE_OPS');
		this.userEid = this.authService.user.externalId;

		this.authorIsExpert = this.authService.isInRole("EXPERT") || this.authService.isInRole("HR_EXPERT");
		this.authorIsKeez = this.authService.isInRole("KEEZ_USER");
		this.isZeus = this.authService.isInRole("ZEUS");
		this.showExpertStartMeeting = this.authService.isInRole("CONNECT_START_VIDEO");
		this.showClientStartMeeting = this.authService.isInRole("CONNECT_CLIENT_START_VIDEO");
		this.startMeetingDisabled = this.startMeetingDisabled ? !this.authService.isInRole("ZEUS") : this.startMeetingDisabled;
	}

	ngOnInit(): void {
		super.ngOnInit();

		this.now = new Date().getTime();

		!this.isClientAdmin && (this._chatRoomSearch = this.authService.user.selectedCompany.companyName);
		this.chatsSubscription = this.chatService.chats.subscribe((chats) => {
			if (this.isFromRefresh) {
				this.isFromRefresh = false;
				this.mergeChats(chats);
				// this.chatService.sortChats(this.chats);
			}
			else {
				this.chats = chats.slice();
			}

			this.chatService.sortChats(this.chats);
			this.filterRooms();
			this.scrollRoomIntoView();
		});

		this.chatTypesSubscription = this.chatService.chatTypes.subscribe((chatTypes) => {
			this.chatTypes = chatTypes.slice();
		});
		this.messagesSubscription = this.chatService.messages.subscribe((messages) => {
			this.messages = messages;
			this.loadAiFeedbackForVisibleAnswers();

			this.prevChunkEid = this.chatService.prevChunkEid;
			this.nextChunkEid = this.chatService.nextChunkEid;

			if (this.loadingInitialChunk) {
				this.displayMessageId = this.chatService.displayMessageId;
			}
			this.onNewMessages();
			if (this.nextChunkEid != null) {
				this.showScrollToBottom = true;
			}
		});
		this.zoneSubscription = this.authService.getSelectedZoneCode().subscribe((zone: string) => {
			this.isClientAdmin = zone === PredefinedUserZone.Administrator;
		});
		this.windowLayoutProps();
		this.resizeSubscription = fromEvent(window, 'resize').subscribe(() => {
			this.windowResize();
		});

		this.dataChangedSubscription = this.chatService.$$dataChanged.subscribe(() => {
			this.detectChanges();
		});
		this.message = this.chatService.restore();

		this.clientFormsService.getFormRoutesAndComponents().pipe(take(1))
			.subscribe((routes) => {
				this.routes = routes;
			});
			
		// Show release notes for CONNECT zone
		this.releaseNotesDialogService.checkAndShowReleaseNotes(AppConstants.ZONE_CONNECT).subscribe();
	}

	public onPuncteLucruHelp(): void {
		this.applicationLinkService.open(ApplicationLinkCode.ConfigurariPuncteLucru);
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		if (this.chatsSubscription) {
			this.chatsSubscription.unsubscribe();
			this.chatsSubscription = null;
		}
		if (this.chatTypesSubscription) {
			this.chatTypesSubscription.unsubscribe();
			this.chatTypesSubscription = null;
		}
		if (this.messagesSubscription) {
			this.messagesSubscription.unsubscribe();
			this.messagesSubscription = null;
		}
		if (this.zoneSubscription) {
			this.zoneSubscription.unsubscribe();
			this.zoneSubscription = null;
		}
		if (this.resizeSubscription) {
			this.resizeSubscription.unsubscribe();
			this.resizeSubscription = null;
		}
		if (this.dataChangedSubscription) {
			this.dataChangedSubscription.unsubscribe();
			this.dataChangedSubscription = null;
		}
		this.unhookMessagesContainer();
		this.chatService.leave(this.message);
	}

	private detectChanges() {
		this.cdr.detectChanges();
	}

	// --- Connect assistant (#12438) --------------------------------------------------------------

	/**
	 * The expert's on-demand ask is switched off (2026-11-05): the scheduled sweep already answers
	 * these channels on its own, and the button's six silent skips read as failures. Everything
	 * behind it - the endpoint, the feature flag, the logging - is untouched, so re-enabling the
	 * functionality is this one value.
	 */
	private readonly askAiEnabled: boolean = false;

	public get canAskAi(): boolean {
		return this.askAiEnabled && (this.isExpert || this.isHrExpert)
			&& !!this.chatService.currentChatRoom && this.aiAnswersEnabled;
	}

	private loadAiAnswersEnabled(chat: ChatModel): void {
		this.aiAnswersEnabled = false;
		if (!this.askAiEnabled || !(this.isExpert || this.isHrExpert)) {
			return;
		}
		this.featureFlagsService.isEnabledForClient$(chat.clientEid, 'ConnectAIAutoAnswer').subscribe((enabled) => {
			if (this.chatService.currentChatRoom?.clientEid === chat.clientEid) {
				this.aiAnswersEnabled = enabled;
				this.detectChanges();
			}
		});
	}

	public get canCommentAiAnswer(): boolean {
		return this.authorIsKeez || this.authorIsExpert;
	}

	/**
	 * Ask the assistant to answer the channel's open question now. The answer, if there is one, arrives
	 * as a normal chat message over the existing websocket, so there is nothing to render here.
	 */
	public requestAiAnswer(): void {
		const room = this.chatService.currentChatRoom;
		if (!room) {
			return;
		}

		this.chatService.requestAiAnswer(room.clientEid, room.chatTypeEid).subscribe(
			() => this.detectChanges(),
			() => this.toastr.error('Asistentul nu a putut fi contactat.')
		);
	}

	// Keyed on the answer handle, not on the author: the assistant also posts the hand-off notice after
	// a rejection, and that is not an answer anyone can rate.
	public isAiAnswer(message: ChatMessageModel): boolean {
		return !!message && !!message.aiAnswerEid;
	}

	/** The current user's own verdict on an answer, or null when they have not voted yet. */
	public ownAiVote(message: ChatMessageModel): string {
		const feedback = this.aiFeedbackByMessageId[message.messageId];
		if (!feedback) {
			return null;
		}
		const mine = feedback.filter((f) => f.reviewerEid === this.authService.user?.externalId && f.vote !== 'Comment');
		return mine.length ? mine[mine.length - 1].vote : null;
	}

	/** Comments are internal to experts and Keez users; the server does not send them to a client either. */
	public aiComments(message: ChatMessageModel): ChatAiFeedbackModel[] {
		const feedback = this.aiFeedbackByMessageId[message.messageId];
		return feedback && this.canCommentAiAnswer ? feedback.filter((f) => !!f.comment) : [];
	}


	/**
	 * Verdicts are fetched only for the assistant answers actually on screen, and only once each. Most
	 * conversations contain none, so folding this into the chunk query would add a field to every chat
	 * read to serve a rare case.
	 */
	private loadAiFeedbackForVisibleAnswers(): void {
		(this.messages || []).filter((m) => this.isAiAnswer(m)).forEach((m) => this.loadAiFeedback(m));
	}

	private loadAiFeedback(message: ChatMessageModel): void {
		const room = this.chatService.currentChatRoom;
		if (!room || !this.isAiAnswer(message) || this.aiFeedbackByMessageId[message.messageId]) {
			return;
		}

		this.aiFeedbackByMessageId[message.messageId] = [];
		this.chatService.getAiAnswerFeedback(room.clientEid, room.chatTypeEid, message.messageId).subscribe((feedback) => {
			this.aiFeedbackByMessageId[message.messageId] = feedback || [];
		this.detectChanges();
		});
	}

	public rateAiAnswer(message: ChatMessageModel, vote: string): void {
		if (this.aiFeedbackSaving) {
			return;
		}
		if (this.canCommentAiAnswer) {
			this.aiFeedbackDialog = { message: message, vote: vote, comment: '' };
			this.detectChanges();
			return;
		}
		this.submitAiVote(message, vote, null);
	}

	public closeAiFeedbackDialog(save: boolean, form: NgForm = null): void {
		if (!save) {
			// Cancelling stays available while a verdict is in flight: it has already been sent, so
			// closing the dialog cannot duplicate it, and a slow call must not trap the dialog open.
			this.aiFeedbackDialog = null;
			this.detectChanges();
			return;
		}
		if (!this.aiFeedbackDialog || !form?.form?.valid || this.aiFeedbackSaving) {
			return;
		}
		const dialog = this.aiFeedbackDialog;
		this.submitAiVote(dialog.message, dialog.vote, dialog.comment, () => {
			this.aiFeedbackDialog = null;
			this.detectChanges();
		});
	}

	private submitAiVote(message: ChatMessageModel, vote: string, comment: string, done?: () => void): void {
		const room = this.chatService.currentChatRoom;
		if (!room || this.aiFeedbackSaving) {
			return;
		}

		// Guarded here as well as at both entry points, so no later caller can start a second send.
		this.aiFeedbackSaving = true;
		this.detectChanges();
		this.chatService.recordAiAnswerFeedback(room.clientEid, room.chatTypeEid, message.messageId, vote, comment).subscribe(
			() => {
				this.aiFeedbackSaving = false;
				done?.();
				// Refresh from the server rather than guessing: a dislike of the last message also re-opens
				// the channel, and the chat list has to reflect that.
				delete this.aiFeedbackByMessageId[message.messageId];
				this.loadAiFeedback(message);
				if (vote === 'Dislike') {
					this.chatService.initializeChats(true, true);
				}
				this.detectChanges();
			},
			() => {
				this.aiFeedbackSaving = false;
				this.detectChanges();
				this.toastr.error('Feedback-ul nu a putut fi salvat.');
			}
		);
	}

	private setTimeout(callback: Function, timeout: number) {
		return this.zone.runOutsideAngular(() => {
			return setTimeout(callback, timeout);
		});
	}

	private clearTimeout(timeout: any) {
		this.zone.runOutsideAngular(() => {
			clearTimeout(timeout);
		});
	}

	private filterRoomsTimeout: any;
	private delayedFilterRooms() {
		const self = this;

		self.chatService.sortChats(self.chats);
		
		if (self.filterRoomsTimeout) {
			self.clearTimeout(self.filterRoomsTimeout);
		}
		self.filterRoomsTimeout = self.setTimeout(() => {
			self.filterRooms();
		}, 500);
	}

	private filterRooms() {
		if (this._chatRoomSearch) {
			const searchText = this._chatRoomSearch.toLowerCase();
			this.filteredChatsTemp = this.chats.filter((c) => 
				this.processString(c.clientName).indexOf(searchText) != -1 || this.processString(c.chatTypeName).indexOf(searchText) != -1 ||
				c.clientName.toLowerCase().indexOf(searchText) != -1 || c.chatTypeName.toLowerCase().indexOf(searchText) != -1);
		} else {
			this.filteredChatsTemp = this.chats.slice();
		}

		this.filterRoomsByStatus(this.filterStatus);

		this.detectChanges();
	}

	public filterRoomsByStatus(filterStatus?: string) {
		this.filterStatus = filterStatus;

		if (filterStatus == ChatStatus.Unanswered) {
			this.filteredChats = this.filteredChatsTemp.filter((c) => c.chatStatus > 0);
		}
		else {
			this.filteredChats = this.filteredChatsTemp.slice();
		}
	}

	private scrollRoomIntoView() {
		this.zone.onStable.pipe(debounceTime(100), take(1)).subscribe(() => {
			const $selectedRoom = $('.room.selected');
			if ($selectedRoom.length) {
				const $chatRoomsList = $('#chatRoomsList');
				const containerScrollTop = $chatRoomsList.scrollTop();
				const containerHeight = $chatRoomsList.height();
				const offsetTop = $selectedRoom[0].offsetTop;
				const deltaTop = offsetTop - containerScrollTop;
				const deltaBottom = containerScrollTop + containerHeight - offsetTop;
				if (deltaTop < -10 || deltaBottom < 100) {
					$selectedRoom[0].scrollIntoView();
				}
			}
		});
	}

	private hookMessagesContainer() {
		if (this._messagesContainerEl) {
			this.scrollSubscription = fromEvent(this._messagesContainerEl, 'scroll')
				.pipe(debounceTime(500))
				.subscribe(() => {
					this.messagesContainerScroll();
					this.onContentScrolled();
				});
		}
	}

	private unhookMessagesContainer() {
		if (this.scrollSubscription) {
			this.scrollSubscription.unsubscribe();
			this.scrollSubscription = null;
		}
	}

	public messageBubbleClass(message: ChatMessageModel, messageIndex: number) {
		let baseClass = 'top-left';
		if (message.authorEid == this.userEid) {
			baseClass += ' author-message';
		}
		if (message.sendFailed) {
			baseClass += ' red-text text-darken-1';
		} else if (!message.messageId && message.messageId != 0) {
			baseClass += ' grey-text text-lighten-1';
		}

		// Determine if this is the first in sequence (has arrow) - check NEXT message
		let isFirstInSequence = true;

		// Check if this message comes after a checkpoint - if so, treat as first
		if (this.isFirstMessageAfterCheckpoint(messageIndex)) {
			isFirstInSequence = true;
		} else if (messageIndex < this.messages.length - 1) {
			const nextMessage = this.messages[messageIndex + 1];
			if (message.authorEid === nextMessage.authorEid && message.datePostedInt == nextMessage.datePostedInt) {
				isFirstInSequence = false;
			}
		}

		// Determine if this is the last in sequence - check PREVIOUS message
		let isLastInSequence = true;
		// If this message has a checkpoint, it's definitely last in sequence (breaks after it)
		if (this.hasVisibleCheckpoint(message)) {
			isLastInSequence = true;
		} else if (messageIndex > 0) {
			const prevMessage = this.messages[messageIndex - 1];
			if (message.authorEid === prevMessage.authorEid && message.datePostedInt == prevMessage.datePostedInt) {
				isLastInSequence = false;
			}
		}

		let classes = baseClass;
		if (isFirstInSequence) {
			classes += ' arrow mt-3';
		}
		if (isLastInSequence) {
			classes += ' last-in-sequence';
		}

		return classes;
	}

	public showMessageAvatarClass(message: ChatMessageModel, messageIndex: number) {
		// Show avatar if this message comes after a checkpoint
		if (this.isFirstMessageAfterCheckpoint(messageIndex)) {
			return false;
		}

		if (messageIndex === this.messages.length - 1) {
			return false;
		}
		const nextMessage = this.messages[messageIndex + 1];
		if (message.authorEid === nextMessage.authorEid && message.datePostedInt == nextMessage.datePostedInt) {
			return true;
		}
		return false;
	}

	public isSameUserMessage(message: ChatMessageModel, messageIndex: number): boolean {
		// Show author name/time if this message comes after a checkpoint
		if (this.isFirstMessageAfterCheckpoint(messageIndex)) {
			return true;
		}

		if (messageIndex === this.messages.length - 1) {
			return true;
		}
		const nextMessage = this.messages[messageIndex + 1];
		if (message.authorEid === nextMessage.authorEid && message.datePostedInt == nextMessage.datePostedInt) {
			return false;
		}
		return true;
	}

	public get isInAnyRoom(): boolean {
		return this.chatService.isInAnyRoom;
	}

	public get showAdminConnectMessageForClient(): boolean {
		const isClient = this.authService.isInRole("CLIENT_ADMIN") && this.authService.selectedZoneCode === PredefinedUserZone.Administrator;
		const isAdminChat = this.chatService.currentChatRoom?.chatTypeEid === 'TECH';
		return showAdminConnectClientMessage && isClient && isAdminChat;
	}

	public get showPayrollConnectMessageForClient(): boolean {
		const isClient = this.authService.isInRole("CLIENT_ADMIN") && this.authService.selectedZoneCode === PredefinedUserZone.Administrator;
		const isPayrollChat = this.chatService.currentChatRoom?.chatTypeEid === 'PAY';
		return showPayrollConnectClientMessage && isClient && isPayrollChat;
	}

	public isCurrent(chat: ChatModel) {
		return this.chatService.currentChatRoom == chat;
	}

	public joinRoom(chat: ChatModel) {
		const currentChatRoom = this.chatService.currentChatRoom;
		if (!currentChatRoom || currentChatRoom.clientEid != chat.clientEid || currentChatRoom.chatTypeEid != chat.chatTypeEid) {
			this.loadingInitialChunk = true;
			this.messages = [];
			this.replyToMessage = null;
			this.prevChunkEid = null;
			this.nextChunkEid = null;
			this.filteredChatTypes = this.chatTypes.filter((ct) => ct.externalId != chat.chatTypeEid);
			this.aiFeedbackByMessageId = {};
			this.aiAnswersEnabled = false;
			this.detectChanges();
			this.chatService.join(chat);
			// After join: a client whose flags were already read answers synchronously, and the guard in
			// loadAiAnswersEnabled compares against the room join has just made current.
			this.loadAiAnswersEnabled(chat);
			this.loadingPrevChunk = false;
			this.loadingNextChunk = false;
		}

		if (!currentChatRoom || currentChatRoom.clientEid != chat.clientEid) {
			this.getClientChangeRequestProcessStatus();
		}

	}

	public sendMessage() {
		//if (this._message) {
			//this.chatService.sendMessageByStomp(this._message);
			const content = (this.replyToMessage ? this.buildReplyQuote(this.replyToMessage) : '') + (this._message || '');
			this.replyToMessage = null;

			if (this.filesForUpload != undefined && this.filesForUpload.length > 0) {
				this.uploadFiles().subscribe((uploadedFiles: UploadFileModel[]) => {
					let successfulFiles = uploadedFiles.filter(f => f?.isUploaded);

					this.chatService.sendMessage(content, successfulFiles);
					this.filesForUpload = [];
					this._message = '';
					this.detectChanges();
					this.delayedResizeTextInput();
				  });
			}
			else {
				this.chatService.sendMessage(content);
				this._message = '';
				this.detectChanges();
				this.delayedResizeTextInput();
			}
		//}
	}

	private onNewMessages() {
		const self = this;
		const scrollToEnd = (): void => {
			if (self._messagesContainerEl) {
				setTimeout(() => {
					self._messagesContainerEl.scrollTo({ top: self._messagesContainerEl.scrollHeight + 10 });
				});
			}
		};
		if (self.loadingInitialChunk) {
			self.detectChanges();
			if (self.displayMessageId && self._unreadMessagesEl) {
				self._unreadMessagesEl.scrollIntoView();
				if (self._unreadMessagesEl.offsetTop > 10) {
					scrollToEnd();
				}
			} else {
				scrollToEnd();
			}
			self.delayedMarkAsRead();
		} else if (self.loadingPrevChunk) {
			const scrollTopOffset = self._messagesContainerEl.scrollHeight - self._messagesContainerEl.scrollTop;
			self.detectChanges();
			self._messagesContainerEl.scrollTo({ top: self._messagesContainerEl.scrollHeight - scrollTopOffset });
		} else if (self.loadingNextChunk) {
			const scrollTop = self._messagesContainerEl.scrollTop;
			self.detectChanges();
			self._messagesContainerEl.scrollTo({ top: scrollTop });
		} else {
			scrollToEnd();
		}
		self.loadingInitialChunk = false;
		self.loadingPrevChunk = false;
		self.loadingNextChunk = false;
		self.detectChanges();
		if (self.prevChunkEid || self.nextChunkEid) {
			self.setTimeout(() => {
				self.checkMessages();
			}, 200);
		}
	}	

	public sendBulk() {
		const self = this;
		for (let i = 0; i < 13; i++) {
			setTimeout(() => {
				self.chatService.sendMessage(`linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}
        linia ${new Date().getTime()}`);
			}, i * 500);
		}
	}

	public textInputKeyDown(e: any) {
		if (e.keyCode === 13 && e.ctrlKey) {
			this._message = (this._message || '') + '\n';
			$('#textInput').val(this._message);
		}
		this.delayedResizeTextInput();
	}

	public textInputKeyPress(e: any) {
		if (e.keyCode === 13 && !e.ctrlKey && !e.shiftKey) {
			this.sendMessage();
			return false;
		}
	}

	private delayedResizeTextInputTimeout: any;
	public delayedResizeTextInput() {
		const self = this;
		if (self.delayedResizeTextInputTimeout) {
			self.clearTimeout(self.delayedResizeTextInputTimeout);
		}
		self.delayedResizeTextInputTimeout = self.setTimeout(() => {
			self.resizeTextInput();
		}, 200);
	}

	private resizeTextInput() {
		const $textInput = $('#textInput');
		$textInput.height(this.getTextSize($textInput.val()));
	}

	private getTextSize(text: string) {
		const $textHiddenInput = $('#textHiddenInput');
		$textHiddenInput.val(text);
		$textHiddenInput.height('auto');
		$textHiddenInput.height($textHiddenInput.prop('scrollHeight'));
		return $textHiddenInput.height();
	}

	private isElementInView(element: any, fullyInView: boolean = false) {
		if ($(element).length > 0) {
			const pageTop = this.layoutProps.containerTop || this.layoutProps.pageTop;
			const pageBottom = this.layoutProps.containerBottom || this.layoutProps.pageBottom;
			const elementTop = $(element).offset().top;
			const elementBottom = elementTop + $(element).height();

			if (fullyInView === true) {
				return pageTop < elementTop && pageBottom > elementBottom;
			} else {
				return elementTop <= pageBottom && elementBottom >= pageTop;
			}
		}
		return false;
	}

	private layoutProps: any = {};

	private windowLayoutProps() {
		const self = this;
		const $window = $(window);
		self.layoutProps.pageHeight = $window.height();
		self.layoutProps.pageTop = 0;
		self.layoutProps.pageBottom = self.layoutProps.pageHeight;
		if (self._messagesContainerEl) {
			const $container = $(self._messagesContainerEl);
			const containerHeight = (self.layoutProps.containerHeight = $container.height());
			const containerTop = (self.layoutProps.containerTop = $container.offset().top);
			self.layoutProps.containerBottom = containerTop + containerHeight;
		} else {
			self.layoutProps.containerHeight = 0;
			self.layoutProps.containerTop = 0;
			self.layoutProps.containerBottom = 0;
		}

		/*
		self.layoutProps.unread = [];
		if (self.displayMessageId) {
			self.layoutProps.unread = self.messages.filter((m) => m.messageId > self.displayMessageId)
				.map((m) => {
					// Updated to use composite ID with clientEid for uniqueness across clients
					const $element = $(`#message${self.chatService.currentChatRoom?.clientEid}_${m.messageId}`);
					const height = $element.height() || 0,
						offsetTop = $element.prop('offsetTop') || 0;
					return {
						messageId: m.messageId,
						height,
						offsetTop,
						isInView(scrollTop: number) {
							let absoluteTop = this.offsetTop,
								absoluteBottom = absoluteTop + this.height,
								deltaTop = absoluteTop - scrollTop,
								deltaBottom = scrollTop + self.layoutProps.containerHeight - absoluteBottom;
							return !!$element.length && deltaTop > -10 && deltaBottom > -10;
						}
					};
				});
		}*/
	}

	private windowResize() {
		this.resizeTextInput();
		this.windowLayoutProps();
	}

	private checkMessagesSubscription: Subscription;
	private checkMessages() {
		let self = this;
		if (this.checkMessagesSubscription) {
			this.checkMessagesSubscription.unsubscribe();
		}
		this.checkMessagesSubscription = this.zone.onStable.pipe(debounceTime(100), take(1)).subscribe(() => {
			if (!this.loadingPrevChunk && this.prevChunkEid && this._prevMessagesLoaderEl && this.isElementInView(this._prevMessagesLoaderEl)) {
				this.loadingPrevChunk = true;
				console.log(`[checkMessages] > prev for prevChunkEid: ${this.prevChunkEid}`);
				this.chatService.prevMessages();
			}
			if (!this.loadingNextChunk && this.nextChunkEid && this._nextMessagesLoaderEl && this.isElementInView(this._nextMessagesLoaderEl)) {
				this.loadingNextChunk = true;
				console.log(`[checkMessages] > next for nextChunkEid: ${this.nextChunkEid}`);
				this.chatService.nextMessages();
			}
		});
	}

	private messagesContainerScroll() {
		console.log("messagesContainerScroll");
		this.checkMessages();
		//this.delayedMarkAsRead();
	}

	private markAsReadTimeout: any;
	private delayedMarkAsRead() {
		const self = this;
		if (self.markAsReadTimeout) {
			self.clearTimeout(self.markAsReadTimeout);
		}
		self.markAsReadTimeout = self.setTimeout(() => {
			self.markAsRead();
		}, 1000);
	}

	private markAsRead() {
		/*if (this.layoutProps && this.layoutProps.unread) {
			const scrollTop = $(this._messagesContainerEl).scrollTop(),
				unreadMessages = this.layoutProps.unread,
				readMessagesIds = unreadMessages.filter((m: any) => m.isInView(scrollTop)).map((m: any) => m.messageId);
			if (readMessagesIds.length) {
				const messageId = Math.max.apply(null, readMessagesIds);
				this.chatService.messageSeen(messageId);
			}
		}*/
		this.chatService.allMessagesSeen();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(e: Event): void {
		if (e.target && (e.target as any).tagName === 'A') {
			const el = e.target as HTMLElement;
			const linkURL = el.getAttribute && el.getAttribute('href');
			if (linkURL) {
				if (linkURL.toLowerCase() === 'https://app.keez.ro/clients/timesheet') {
					e.preventDefault();
					this.router.navigate(['/clients/timesheet']);
				}
				else if (linkURL.toLowerCase().includes(clientDocumentRouteUrl)) {
					e.preventDefault();
					this.getDocumentAndOpen(linkURL, clientDocumentRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(expertDocumentRouteUrl)) {
					e.preventDefault();
					this.getDocumentAndOpen(linkURL, expertDocumentRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(payrollDocumentRouteUrl)) {
					e.preventDefault();
					this.getDocumentAndOpen(linkURL, payrollDocumentRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(microProfitFormRouteUrl.form) && linkURL.toLowerCase().includes(microProfitFormRouteUrl.microProfit)) {
					e.preventDefault();
					this.getMicroProfitFormDataAndOpen(linkURL, microProfitFormRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(mp2024FormRouteUrl.form) && linkURL.toLowerCase().includes(mp2024FormRouteUrl.mp2024)) {
					e.preventDefault();
					this.getMp2024FormDataAndOpen(linkURL, mp2024FormRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(duPFAFormRouteUrl.form) && linkURL.toLowerCase().includes(duPFAFormRouteUrl.duPFA)) {
					e.preventDefault();
					this.getDuPFAFormDataAndOpen(linkURL, duPFAFormRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(meetingRequestRouteUrl.base) && linkURL.toLowerCase().includes(meetingRequestRouteUrl.meetings)) {
					e.preventDefault();
					this.getMeetingRequestAndOpen(linkURL, meetingRequestRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(meetingRouteUrl)) {
					e.preventDefault();
					this.getMeetingDataAndOpen(linkURL, meetingRouteUrl);
				}
				else if (linkURL.toLowerCase().includes(eshopOrderRouteUrl)) {
					e.preventDefault();
					this.navigateToEshopOrder(linkURL, eshopOrderRouteUrl);
				}
			}
			// else if (linkURL && linkURL.toLowerCase().startsWith("https://app.keez.ro/clients/accdocuments/locate")) {
			// 	e.preventDefault();
			// 	this.router.navigate(['/clients', 'accdocuments', 'locate', 'f469152e9d754bd3be6ff56318f4a1b0']);
			// }
		}

		// if (e.target && ((e.target as any).className).includes('k-overlay')) {
		// 	if (this.showDocumentDialog) {
		// 		this.closeDocumentDialog();
		// 	}
		// }
	}

	@HostListener("keydown", ["$event"])
	public keydown(event: any): void {
		if (event.keyCode === 27) {
			if (this.showDocumentDialog) {
				this.closeDocumentDialog();
			}
			if (this.showMicroProfitForm) {
				this.closeMicroProfitFormDialog();
			}
			if (this.showDuPFAForm) {
				this.closeDuPFAFormDialog();
			}
			if (this.showDuPFA2024Form) {
				this.closeDuPFA2024FormDialog();
			}
			if (this.showDuPFA2025Form) {
				this.closeDuPFA2025FormDialog();
			}
			if (this.showMp2024Form) {
				this.closeMp2024FormDialog();
			}
		}
	}

	public getDocumentAndOpen(linkURL: string, urlSourceRoute: string) {
		let urlStartPosition = linkURL.indexOf(`${urlSourceRoute}`) + urlSourceRoute.length;
		let documentEid = linkURL.substring(urlStartPosition);

		this.documentIndentification = new DocumentIndentificationModel();
		this.documentIndentification.externalId = documentEid;
		this.documentIndentification.clientEid = this.chatService.currentChatRoom.clientEid;

		if (urlSourceRoute == clientDocumentRouteUrl || urlSourceRoute == expertDocumentRouteUrl) {
			this.documentIndentification.documentType = "accounting";
		}
		else if (urlSourceRoute == payrollDocumentRouteUrl) {
			this.documentIndentification.documentType = "payroll";
		}

		this.openDocumentDialog();
	}

	public openDocumentDialog() {
		this.showDocumentDialog = true;
		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeDocumentDialog() {
		this.showDocumentDialog = false;
	}

	public getMp2024FormDataAndOpen(linkURL: string, urlSourceRoute: any) {
		this.clientEid = linkURL.substring(linkURL.indexOf(`${urlSourceRoute.form}`) + urlSourceRoute.form.length, linkURL.indexOf(`${urlSourceRoute.mp2024}`));
		let instanceStartPosition = linkURL.indexOf(`${urlSourceRoute.mp2024}`) + `${urlSourceRoute.mp2024}`.length;
		this.instance = linkURL.substring(instanceStartPosition);

		let found = this.routes.find((r) => r.formCode === this.instance.toLowerCase());

		if (found) {
			if (found.dataJsonParsed != null) {
				this.is202503Form = found.dataJsonParsed.is202503Form;
			}
			this.mapComponentToDialog(found.componentCode);
		} else {
			this.toastr.error('Formularul nu este configurat in baza de date.');
		}
	}

	private mapComponentToDialog(componentName: string): void {
		switch (componentName) {
			case 'MP2025P2MFormComponent':
				this.openMp2025P2MFormDialog();
				break;
			case 'MP20242025M2PFormComponent':
				this.openMp20242025M2PFormDialog();
				break;
			case 'MP2024FormComponent':
				this.openMp2024FormDialog();
				break;
			default:
				break;
		}
	}

	public openMp2024FormDialog() {
		this.showMp2024Form = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeMp2024FormDialog() {
		this.showMp2024Form = false;
		this.clearFormInputs();
	}

	public openMp2025P2MFormDialog() {
		this.showMp2025P2MForm = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeMp2025P2MFormDialog() {
		this.showMp2025P2MForm = false;
		this.clearFormInputs();
	}

	public openMp20242025M2PFormDialog() {
		this.showMp20242025M2PForm = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeMp20242025M2PFormDialog() {
		this.showMp20242025M2PForm = false;
		this.clearFormInputs();
	}

	public getDuPFAFormDataAndOpen(linkURL: string, urlSourceRoute: any) {
		this.clientEid = linkURL.substring(linkURL.indexOf(`${urlSourceRoute.form}`) + urlSourceRoute.form.length, linkURL.indexOf(`${urlSourceRoute.duPFA}`));
		let instanceStartPosition = linkURL.indexOf(`${urlSourceRoute.duPFA}`) + `${urlSourceRoute.duPFA}`.length;
		this.instance = linkURL.substring(instanceStartPosition);

		let found = this.routes.find((r) => r.formCode === this.instance.toLowerCase());
		if (found) {
			this.mapDuPFAComponentToDialog(found.componentCode);
		} else {
			this.toastr.error('Formularul nu este configurat in baza de date.');
		}
	}

	private mapDuPFAComponentToDialog(componentName: string): void {
		switch (componentName) {
			case 'DuPFA2024FormComponent':
				this.openDuPFA2024FormDialog();
				break;
			case 'DuPFA2025FormComponent':
				this.openDuPFA2025FormDialog();
				break;
			case 'DuPFAKeezFormComponent':
				this.openDuPFAFormDialog();
				break;
			default:
				break;
		}
	}

	public openDuPFAFormDialog() {
		this.showDuPFAForm = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public openDuPFA2024FormDialog() {
		this.showDuPFA2024Form = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeDuPFAFormDialog() {
		this.showDuPFAForm = false;
		this.clearFormInputs();
	}

	public closeDuPFA2024FormDialog() {
		this.showDuPFA2024Form = false;
		this.clearFormInputs();
	}

	public openDuPFA2025FormDialog() {
		this.showDuPFA2025Form = true;

		setTimeout(() => {
			this.detectChanges();
		}, 200);
	}

	public closeDuPFA2025FormDialog() {
		this.showDuPFA2025Form = false;
		this.clearFormInputs();
	}

	public getMicroProfitFormDataAndOpen(linkURL: string, urlSourceRoute: any) {
		this.clientEid = linkURL.substring(linkURL.indexOf(`${urlSourceRoute.form}`) + urlSourceRoute.form.length, linkURL.indexOf(`${urlSourceRoute.microProfit}`));
		let instanceStartPosition = linkURL.indexOf(`${urlSourceRoute.microProfit}`) + `${urlSourceRoute.microProfit}`.length;
		this.instance = linkURL.substring(instanceStartPosition);

		//this.router.navigate([CLIENT_FORMS_ROUTES.ROOT, this.clientEid, CLIENT_FORMS_ROUTES.MICRO_PROFIT_FORM, this.instance]);
		this.openMicroProfitFormDialog();
	}

	public openMicroProfitFormDialog() {
		this.showMicroProfitForm = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeMicroProfitFormDialog() {
		this.showMicroProfitForm = false;
		this.clearFormInputs();
	}

	public async getMeetingDataAndOpen(linkURL: string, urlSourceRoute: any) {
		let urlStartPosition = linkURL.indexOf(`${urlSourceRoute}`) + urlSourceRoute.length;
		let eid = linkURL.substring(urlStartPosition);

		let token = await this.chatService.joinVideoChatMeeting(eid);
		if (token) {
			console.log("meetingToken: " + token);
			let url = `${window.location.origin}/xmeeting/join/${token}`;
			window.open(url, '_blank', 'noopener noreferrer');
		}
	}

	public navigateToEshopOrder(linkURL: string, urlSourceRoute: any) {
		let urlStartPosition = linkURL.toLowerCase().indexOf(`${urlSourceRoute}`) + urlSourceRoute.length;
		let eid = linkURL.substring(urlStartPosition).split('?')[0].split('#')[0];

		if (eid) {
			this.router.navigate([ESHOP_ROUTES.ROOT, ESHOP_ROUTES.ORDERS, eid]);
		}
	}

	public clearFormInputs() {
		this.clientEid = null;
		this.instance = null;
	}

	public markdown(message: string): string {
		if (message && message.trim() !== '') {
			return markdown(message);
		}
	}

	public replyMessage(messageModel: ChatMessageModel): void {
		this.replyToMessage = messageModel;
		this.detectChanges();
		$('#textInput').focus();
	}

	public cancelReply(): void {
		this.replyToMessage = null;
	}

	public getAttachmentsSummary(docs: ChatMessageAttachedDocumentModel[]): string {
		if (!docs || !docs.length) {
			return null;
		}

		const count = docs.length;
		const countPrefix = count >= 20 ? `${count} de` : `${count}`;
		if (docs.every(d => this.isImage(d.documentFileName))) {
			return count === 1 ? 'Fotografie' : `${countPrefix} fotografii`;
		}
		return count === 1 ? docs[0].documentFileName : `${countPrefix} fișiere`;
	}

	// thumbnail of the first attachment when all attachments are the same type; generic file icon when types are mixed
	public getReplyPreview(messageModel: ChatMessageModel): { thumbnail?: string, iconClass?: string } {
		const docs = messageModel.attachedDocuments;
		if (!docs?.length) {
			return null;
		}

		const extension = (fileName: string) => fileName.split('.').pop().toLowerCase();
		const allImages = docs.every(d => this.isImage(d.documentFileName));
		const sameType = allImages || docs.every(d => extension(d.documentFileName) === extension(docs[0].documentFileName));
		if (!sameType) {
			return { iconClass: 'general-document' };
		}

		const first = docs[0];
		if (this.isImage(first.documentFileName) || this.isPdf(first.documentFileName)) {
			return { thumbnail: this.getAttachmentPreview(messageModel, first) };
		}
		return { iconClass: this.getFileIcon(first.documentFileName) };
	}

	private buildReplyQuote(messageModel: ChatMessageModel): string {
		const datepipe: DatePipe = new DatePipe(this.intlService);
		const formattedDate = datepipe.transform(messageModel.datePosted, 'dd.MM.yyyy kk:mm');

		const parts = [`**${messageModel.authorName}** - ${formattedDate}`];
		const summary = this.getAttachmentsSummary(messageModel.attachedDocuments);
		if (summary) {
			parts.push(summary);
		}
		if (messageModel.content) {
			parts.push(messageModel.content.replace(/\r\n/g, '\n'));
		}

		return '> ' + parts.join('\n\n').split('\n').join('\n> ') + '\n\n';
	}

	public moveMessage(message: ChatMessageModel, chatType: ChatTypeModel): void {
		this.chatService.moveMessageStream(message.messageId, chatType.externalId).pipe(take(1)).subscribe();
	}

	public notificationDotColor(chat: ChatModel) {
		// if (this.isSpecialCareOps) {
		// 	return 'unanswered-count-color0';
		// }
		// else
		if (chat.clientInSpecialCare && chat.chatStatus >= 1) {
			return 'unanswered-count-color3';
		}
		else {
			return this.notificationCountClass(chat.chatStatus);
		}
	}

	public notificationCountClass(chatStatus: number): string {
		var colorClass;
		switch (chatStatus) {
			case 0:
				colorClass = 'unanswered-count-color0';
				break;
			case 1:
				colorClass = 'unanswered-count-color1';
				break;
			case 2:
				colorClass = 'unanswered-count-color2';
				break;
			case 3:
				colorClass = 'unanswered-count-color3';
				break;
			default:
				colorClass = 'unanswered-count-color0'
				break;
		}

		return colorClass
	}

	public refresh() {
		this.isFromRefresh = true;
		this.chatService.initializeChats(true, true);
	}

	public markClientChatUrgent(chat: ChatModel) {
		var chatInputModel = new ClientChatInputModel();
		chatInputModel.clientEid = chat.clientEid;
		chatInputModel.chatExternalId = chat.chatTypeEid;
		
		this.chatService.markClientChatUrgent(chatInputModel)
			.pipe(take(1))
			.subscribe((response: boolean) => {
				if (response) {
					// CM 20211008
					//this.refresh();
					chat.setUrgent();
					this.detectChanges();
					this.toastr.success('Chat-ul a fost marcat important!');
				}
			});
	}

	public keepClientChatImportant(chat: ChatModel) {
		var chatInputModel = new ClientChatInputModel();
		chatInputModel.clientEid = chat.clientEid;
		chatInputModel.chatExternalId = chat.chatTypeEid;

		this.chatService.markClientChatImportant(chatInputModel)
			.pipe(take(1))
			.subscribe((response: boolean) => {
				if (response) {
					// CM 20211008
					//this.refresh();
					chat.keepImportant();
					this.detectChanges();
					this.toastr.success('Chat-ul a fost marcat nerezolvat!');
				}
			});
	}

	public markChatAnswered(chat: ChatModel) {
		// this.chatAnsweredInputModel = new ClientChatCommentInputModel();
		// this.chatAnsweredInputModel.clientEid = chat.clientEid;
		// this.chatAnsweredInputModel.chatExternalId = chat.chatTypeEid;

		const isHrExpertInPayroll = this.isHrExpert && chat.chatTypeEid === 'PAY';

		if (chat.chatStatus == 3 || isHrExpertInPayroll) {
			this.chatOpInput = new ChatEditCommentModel();
			this.chatOpInput.chat = chat;

			this.showMarkChatAnsweredDialog = true;
		}
		else {
			this.markClientChatAnswered(new ChatEditCommentModel({ chat: chat}));
		}
	}

	public markClientChatAnswered(chat: ChatEditCommentModel) {
		var chatInputModel = new ClientChatCommentInputModel();
		chatInputModel.clientEid = chat.chat.clientEid;
		chatInputModel.chatExternalId = chat.chat.chatTypeEid;
		chatInputModel.comment = chat.comment;

		this.chatService.markClientChatAnswered(chatInputModel)
			.pipe(take(1))
			.subscribe((response: boolean) => {
				if (response) {
					this.showMarkChatAnsweredDialog = false;
					chat.chat.setAnswered();
					this.detectChanges();
					// CM 20211008 do not refresh, just set chat properties accordingly
					//this.refresh();
					//this.chatAnsweredInputModel.chat.setAnswered();
					this.toastr.success('Chat-ul a fost marcat rezolvat!');
				}
			});
	}

	public closeMarkChatAnsweredDialog(save: boolean, chatCommentForm: NgForm = null) {
		if (save && chatCommentForm != null) {
			if (chatCommentForm.form != null && chatCommentForm.form.valid) {
				this.markClientChatAnswered(this.chatOpInput);
			}
		} else {
			this.showMarkChatAnsweredDialog = false;
		}
	}

	private mergeChats(chats: ChatModel[]) {
		this.chats = chats.slice();
		
		// for (var i=0;i<this.chats.length;i++) {
		// // this.chats.forEach((c) => {
		// 	var c = this.chats[i];
		// 	chats.forEach((nc) => {
		// 		if (c.clientEid == nc.clientEid && c.chatTypeEid == nc.chatTypeEid) {
		// 			// c.chatStatus = nc.chatStatus;
		// 			// c.firstUnseenMessage = nc.firstUnseenMessage;
		// 			// c.firstUnansweredMessage = nc.firstUnansweredMessage;
		// 			// c.lastMessageDelivered = nc.lastMessageDelivered;
		// 			// c.unseenMessages = nc.unseenMessages;
		// 			// c.unansweredMessages = nc.unansweredMessages;
		// 			this.chats[i] = nc;
		// 		}
		// 	})
		// }
		// //);
	}

	public onMessagesCountMouseOver(chatStatus: number): string {
		let messageTooltip: string;
		switch (chatStatus) {
			case 3:
				messageTooltip = "Urgent";
				break;
			case 2:
				messageTooltip = "Important";
				break;
			case 1:
				messageTooltip = "Așteaptă răspuns";
				break;
			case 0:
				messageTooltip = "Există răspuns";
				break;
			default:
				messageTooltip = "";
				break;
		}

		return messageTooltip;
	}

	private processString(text: string): string {
		var str;
		// remove accents/diacritics
		str = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

		// lower case
		return str.toLowerCase();
	}

	public async requestVideo() {
		if (this.startMeetingDisabled) {
			return;
		}

		//by Expert
		if (this.showExpertStartMeeting) {
			let m = new Meetings.CreateMeetingGQL(this.restService);
			await m.mutate({
				data: {
					chatTypeEid: this.chatService.currentChatRoom.chatTypeEid,
					clientEid: this.chatService.currentChatRoom.clientEid
				}
			}).toPromise();
		}
		//by Client
		else if (this.showClientStartMeeting && this.chatService.isFINChatRoom) {
			this.showMeetingInfo = true;
		}

		// this.refresh();
		// this.chatAnsweredInputModel.chat.setAnswered();
		// this.chatService.initializeChats(true, false);
	}

	public closeMeetingDialogInfo(agreed: boolean) {
		if (agreed) {
			this.openMeetingCalendarDialog();
		}

		this.showMeetingInfo = false;
	}

	public async openMeetingCalendarDialog(meetingRequestData?: MeetingRequestDataModel, fromProposal: boolean = false) {
		let self = this;
		let today = new Date(), tomorrow = new Date();
		tomorrow.setDate(today.getDate() + 1); //tomorrow

		this.meetingRequest = new MeetingRequestModel();
		this.meetingRequest.chatTypeEid = this.chatService.currentChatRoom.chatTypeEid;
		this.meetingRequest.dateEx = tomorrow;

		if (meetingRequestData == null) {
			this.meetingRequest.meetingRequestEid = UID();
		}
		else {
			this.meetingRequest.meetingRequestEid = meetingRequestData.meetingRequestEid;
			this.meetingRequest.comment = meetingRequestData.comment;
		}

		this.fromProposal = fromProposal;

		this.expertInfoPersonModel = await this.chatService.getMainClientExpertInfo(self.authService.user.selectedCompanyId);
		if (meetingRequestData != null && meetingRequestData.history != null) {
			this.meetingInfoLastHistory = meetingRequestData.history;
		}

		this.showMeetingCalendar = true;
		this.detectChanges();
	}

	public async closeMeetingCalendarDialog(save: boolean, meetingRequestModel?: any) {
		let meetingRequestEid;

		if (save && meetingRequestModel) {
			if (!meetingRequestModel.fromProposal) {
				meetingRequestEid = await this.chatService.requestMeeting(this.chatService.currentChatRoom.clientEid, meetingRequestModel.meetingRequest);
			}
			else {
				meetingRequestEid = await this.chatService.proposeNewMeeting(this.chatService.currentChatRoom.clientEid, meetingRequestModel.meetingRequest);
			}
		}

		this.showMeetingCalendar = false;
	}

	public getMeetingRequestAndOpen(linkURL: string, urlSourceRoute: any) {
		let clientEid = linkURL.substring(linkURL.indexOf(`${urlSourceRoute.base}`) + urlSourceRoute.base.length, linkURL.indexOf(`${urlSourceRoute.meetings}`));
		let meetingStartPosition = linkURL.indexOf(`${urlSourceRoute.meetings}`) + `${urlSourceRoute.meetings}`.length;
		let meetingRequestEid = linkURL.substring(meetingStartPosition);

		this.openMeetingInfo(clientEid, meetingRequestEid);
	}

	public async openMeetingInfo(clientEid: string, meetingRequestEid: string) {
		this.expertInfoPersonModel = await this.chatService.getMainClientExpertInfo(this.authService.user.selectedCompanyId);
		this.meetingRequestData = await this.chatService.getMeetingRequestData(clientEid, meetingRequestEid);
		this.showMeetingRequest = true;

		setTimeout(() => {
			this.detectChanges();	
		}, 200);
	}

	public closeMeetingRequestDataDialog() {
		this.showMeetingRequest = false;
	}

	public async acceptMeetingRequest(meetingRequestData: MeetingRequestDataModel) {
		let meetingRequestEid = await this.chatService.acceptMeetingRequest(meetingRequestData.clientEid, meetingRequestData.meetingRequestEid);
		if (meetingRequestEid) {
			this.toastr.success("Întâlnirea a fost acceptată.");
		}

		this.closeMeetingRequestDataDialog();
	}

	public async cancelMeetingRequest(meetingRequestData: MeetingRequestDataModel) {
		let result = await this.chatService.cancelMeetingRequest(meetingRequestData.clientEid, meetingRequestData.meetingRequestEid);
		if (result) {
			this.toastr.success("Întâlnirea a fost anulată.");
		}

		this.closeMeetingRequestDataDialog();
	}

	public changeMeetingRequestInterval(meetingRequestData: MeetingRequestDataModel) {
		this.closeMeetingRequestDataDialog();
		this.openMeetingCalendarDialog(meetingRequestData, true);
	}

	onContentScrolled() {
		const scrollTopOffset = this._messagesContainerEl.scrollHeight - this._messagesContainerEl.scrollTop;
		if (scrollTopOffset > 900) {
			this.showScrollToBottom = true;
		}

		if (this.nextChunkEid == null && this._messagesContainerEl.scrollHeight - this._messagesContainerEl.scrollTop - this._messagesContainerEl.clientHeight <= 10) {
			this.showScrollToBottom = false;
		}

		this.detectChanges();
	}

	scrollToBottomV1() {
		let self = this;
		if (this.nextChunkEid != null) {
			setTimeout(() => {
				this.loadingInitialChunk = true;
				this.messages = [];
				this.chatService.initialMessages()
					.pipe(
						first()
					)
					.subscribe((messages: ChatMessageModel[]) => {
						if (messages) {
							this.chatService.messages.next(messages.reverse());
						} else {
							this.chatService.messages.next([]);
						}

						if (self._messagesContainerEl) {
							self._messagesContainerEl.scrollTo({ top: self._messagesContainerEl.scrollHeight + 10 });
							this.showScrollToBottom = false;
						}
					});

				this.loadingPrevChunk = false;
				this.loadingNextChunk = false;
			});
		}
		else {
			if (self._messagesContainerEl) {
				self._messagesContainerEl.scrollTo({ top: self._messagesContainerEl.scrollHeight + 10 });
				this.showScrollToBottom = false;
			}
		}		
	}

	scrollToBottom() {
		let self = this;

		setTimeout(() => {
			self.loadingInitialChunk = true;
			self.messages = [];
			
			self.chatService.initialMessages()
				.pipe(
					first()
				)
				.subscribe((messages: ChatMessageModel[]) => {
					if (messages) {
						self.chatService.messages.next(messages.reverse());
					} else {
						self.chatService.messages.next([]);
					}
				});

			self.loadingPrevChunk = false;
			self.loadingNextChunk = false;
		});
	}

	public getClientChangeRequestProcessStatus() {
		this.chatService.getClientChangeRequestProcessStatus(this.chatService.currentChatRoom.clientEid)
			.pipe(take(1))
			.subscribe((data) => {
				if (data) {
					if (data.cancellationProcess != null) {
						this.clientChangeRequestProcessString = "Procesul de reziliere"
						if (data.cancellationProcess.type === 'InitiatedByLimitedResurrection') {
							this.clientChangeRequestProcessString += " (din activare limitată)"
						}
						if (data.cancellationProcess.momentCancellationInitiated) {
							this.clientChangeRequestProcessString += `, inițiat în ${data.cancellationProcess.momentCancellationInitiated}`
						}
						this.clientChangeRequestProcessString += " este în desfășurare.";
					}
					else if (data.dissolutionProcess != null) {
						this.clientChangeRequestProcessString = "Procesul de reziliere (lichidare)"
						if (data.dissolutionProcess.momentCancellationInitiated ) {
							this.clientChangeRequestProcessString += `, inițiat în ${data.dissolutionProcess.momentCancellationInitiated}`
						}
						this.clientChangeRequestProcessString += " este în desfășurare.";
					}
					else {
						this.clientChangeRequestProcessString = null;
					}
				}
				else {
					this.clientChangeRequestProcessString = null;
				}

				this.cdr.detectChanges();
			})
	}

	public hasCheckpointTriggerResolved(message: ChatMessageModel) {
		return message.checkpoint != null && message.checkpoint.messageCheckPointTrigger == 'ChatAction' && message.checkpoint.messageCheckPointType == 'Resolved';
	}

	// The assistant gave this question up - it could not answer from the knowledge base, the client
	// rejected its answer, or someone replied saying they would take it. Deliberately not the green
	// tick used for a resolved question: nothing was resolved, and the question still needs a person.
	public hasCheckpointDeferred(message: ChatMessageModel) {
		return message.checkpoint != null && message.checkpoint.messageCheckPointType == 'Deferred';
	}

	// Any checkpoint that draws its own row in the thread. Grouping keys on this rather than on the
	// resolved one alone, so a marker never lands in the middle of a run of messages.
	public hasVisibleCheckpoint(message: ChatMessageModel) {
		return this.hasCheckpointTriggerResolved(message) || this.hasCheckpointDeferred(message);
	}

	public isFirstMessageAfterCheckpoint(messageIndex: number): boolean {
		// Messages are in reverse order (higher index = older/displayed above)
		// Check if the message ABOVE (higher index) has a checkpoint
		if (messageIndex >= this.messages.length - 1) return false;
		const messageAbove = this.messages[messageIndex + 1];
		return this.hasVisibleCheckpoint(messageAbove);
	}

	public onMessageAvatarError(event: any) {
		event.target.onerror = '';
		const src: string = event.target.src;
		if (!src || !src.endsWith('/public/images/user-image.png')) {
			event.target.src = '/public/images/user-image.png';
			return true;
		}
		return false;
	}

	public onAttachmentPreviewError(event: any) {
		event.target.onerror = '';
		event.target.src = '/public/images/document.png';
		return true;
	}

	public getAttachmentPreview(message: ChatMessageModel, attachment: ChatMessageAttachedDocumentModel) {
		return `/chats/${this.chatService.currentChatRoom.clientEid}/${this.chatService.currentChatRoom.chatTypeEid}/messages/${message.messageId}/documents/${attachment.documentEid}/thumbnail`;
	}

	public openAttachmentPreview(message: ChatMessageModel, attachment: ChatMessageAttachedDocumentModel) {
		this.getPdfSrc(message, attachment);
		this.selectedAttachment = attachment;
		this.selectedMessage = message;
		this.showAttachmentPreview = true;
	}

	public closeAttachmentPreview() {
		this.selectedAttachment = null;
		this.selectedMessage = null;
		this.showAttachmentPreview = false;
		this.setDefaultZoom();
	}

	public getFileIcon(fileName: string) {
		return getFileIcon(fileName);
    }

	public isImage(fileName: string) {
		let fileExtension = fileName.split('.').pop().toLowerCase();
		return this.imageFileType.includes(fileExtension);
	}

	public isPdf(fileName: string) {
		let fileExtension = fileName.split('.').pop().toLowerCase();
		return fileExtension === 'pdf';
	}

	onPaste(event: ClipboardEvent) {
		this.delayedResizeTextInput();

		let items = event.clipboardData?.items;
		if (!items) return;

		for (let i = 0; i < items.length; i++) {
			if (items[i].type === 'text/plain') {
				break;
			}

		  	let file = items[i].getAsFile();
		  	this.handleAttachedFile(file);
		}
	}

	public handleAttachedFile(file: File) {
		let fileTypeLabel = this.allowedFileTypes.get(file.type);
		let fileExists = this.filesForUpload.find((f) => f.file.name == file.name);

		if (file && (file.type.startsWith('image/') || this.allowedFileTypes.has(file.type)) && !fileExists) {
			let fileForUpload: UploadFileModel = {
				file: file,
				fileType: fileTypeLabel || 'image',
				isImage: file.type.startsWith('image/'),
				isUploaded: false
			};

			if (fileForUpload.isImage) {
				let reader = new FileReader();
				reader.onload = e => {
					fileForUpload.previewSrc = (e.target as any).result;
					this.filesForUpload.push(fileForUpload);
					this.cdr.detectChanges();
				};
				reader.readAsDataURL(file);
			}
			else {
				this.filesForUpload.push(fileForUpload);
			}
		  }
	}

	public triggerFilesAttachment(): void {
		if (this.filesAttachment) {
			// Build accept attribute from allowedFileTypes and imageFileType
			const mimeTypes = Array.from(this.allowedFileTypes.keys());
			const imageExtensions = this.imageFileType.map(ext => `.${ext}`);
			const acceptValue = [...mimeTypes, ...imageExtensions].join(',');

			this.filesAttachment.nativeElement.setAttribute('accept', acceptValue);
			this.filesAttachment.nativeElement.click();
		}
	}

	public onFilesAttachmentSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const files = input.files;

		if (!files) return;

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			this.handleAttachedFile(file);
		}

		input.value = '';
	}

	public uploadFiles() {
		let observables: Observable<any>[] = [];
		let errors: any[] = [];

		this.filesForUpload.forEach((uploadFile: UploadFileModel) => {
			observables.push(this.chatService
				.uploadFileStream(uploadFile)
				.pipe(
					catchError((error) => {
						errors.push(`Eroare la încărcarea fișierului '${uploadFile.file.name}'`);
						return of(null);
					}))
			);
		});

		return forkJoin(observables)
			.pipe(
				map((resultFiles: UploadFileModel[]) => {
					if (errors && errors.length) {
						var errorMessage = '';
						errors.forEach((error) => {
							errorMessage += `<li>${error}</li>`;
						});
						this.toastr.error(`<ul>${errorMessage}</ul>`, 'Eroare la încărcarea unor fișiere', {
							enableHtml: true,
							messageClass: 'pl-3'
						});
					}
				
					return resultFiles.filter((r: UploadFileModel) => r !== null);
				}),
				catchError((err) => {
					this.toastr.error('Eroare neașteptată la încărcarea fișierelor.');
					return of([]);
				})
			)
	}

	public removeUpload(upload: UploadFileModel) {
		this.filesForUpload = this.filesForUpload.filter(u => u !== upload);
	}

	public downloadDocument(message: ChatMessageModel, attachment: ChatMessageAttachedDocumentModel) {
		let url = `/chats/${this.chatService.currentChatRoom.clientEid}/${this.chatService.currentChatRoom.chatTypeEid}/messages/${message.messageId}/documents/${attachment.documentEid}/download`;
		this.downloadService.download(url).pipe(take(1)).subscribe();
	}

	//#region PdfViewer
	public getPdfSrc(message: ChatMessageModel, attachment: ChatMessageAttachedDocumentModel) {
		this.pdfSrc = `/chats/${this.chatService.currentChatRoom.clientEid}/${this.chatService.currentChatRoom.chatTypeEid}/messages/${message.messageId}/documents/${attachment.documentEid}/download`;
	}

	public zoomIn() {
        this.zoom += 0.1;
        this.handlePropertiesOnZoom();
    }

    public zoomOut() {
        this.zoom -= 0.1;
        this.handlePropertiesOnZoom();
    }

	public setDefaultZoom() {
		this.zoom = DEFAULT_PDF_ZOOM;
		this.handlePropertiesOnZoom();
	}

	public handlePropertiesOnZoom() {
        var pdfViewerContainer = this.elementRef.nativeElement.querySelector(".ng2-pdf-viewer-container");
		if (pdfViewerContainer != null) {
            if (this.zoom > 1) {
                pdfViewerContainer.setAttribute("style", "display: block !important;");
            }
            else {
                pdfViewerContainer.setAttribute("style", "display: flex !important; align-items: center !important; justify-content: center !important");
            }
        }
    }

    public nextPage() {
        this.page++;
    }

    public previousPage() {
        this.page--;
    }

	public setFullScreen() {
		this.fullScreen = !this.fullScreen;
		if (!this.fullScreen) {
			this.setDefaultZoom();
		}
	}
	
	public onDrag(event: MouseEvent) {
		var pdfViewerContainer = this.elementRef.nativeElement.querySelector(".ng2-pdf-viewer-container");

		if (this.isDragging && pdfViewerContainer) {
			let x = pdfViewerContainer.scrollLeft - event.movementX;
			let y = pdfViewerContainer.scrollTop - event.movementY;
			
			pdfViewerContainer.scrollTo(x, y);
		}
	}

	public onDragStarted(event: MouseEvent) {
		if ((event.target as HTMLElement).nodeName == 'SPAN') {
			this.isDragging = false;
		}
		else {
			this.isDragging = true;
			document.onmousemove = this.documentOnDrag.bind(this);
			document.onmouseup = this.onDragEnded.bind(this);
		}		
	}

	public onDragEnded(event: MouseEvent ) {
		this.isDragging = false;
		document.onmousemove = null;
		document.onmouseup = null;
	}

	public documentOnDrag(event: MouseEvent) {
		this.onDrag(event);
	}

	public onError(event: any) {
        console.log(event);
    }
	//#endregion PdfViewer

	/**
	 * Show release notes for a specific zone using the dynamic dialog service
	 * @param zone The zone identifier (e.g., "CONNECT")
	 */
	// public showReleaseNotes(zone: string): void {
	// 	// Use the dynamic dialog service to check and show release notes
	// 	this.releaseNotesDialogService.checkAndShowReleaseNotes(zone).subscribe({
	// 		next: (dialogShown) => {
	// 			if (dialogShown) {
	// 				console.log('Release notes dialog shown for zone:', zone);
	// 			}
	// 		},
	// 		error: (error) => {
	// 			console.error('Error showing release notes:', error);
	// 		}
	// 	});
	// }
}

